// ═══════════════════════════════════════════════════════════════════════════════
// SKYBOT MILITARY-GRADE AI-ENHANCED FILTER SYSTEM
// Version 2.0 - Eclipse Safe Viewing + Advanced Optics
// ═══════════════════════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────────────────────────
// SOLAR OBSERVATION FILTERS - ECLIPSE SAFE VIEWING
// Based on ISO 12312-2 safety standards for solar viewing
// ─────────────────────────────────────────────────────────────────────────────────

export const liveFilterStyles = {
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // SOLAR ECLIPSE SAFE VIEWING - ISO 12312-2 COMPLIANT
  // WARNING: Digital filters alone are NOT sufficient for direct solar viewing
  // These provide enhanced viewing of solar phenomena through proper equipment
  // ═══════════════════════════════════════════════════════════════════════════════
  
  solar_eclipse: {
    filter: 'brightness(0.08) contrast(3.5) saturate(0.2) sepia(0.3) hue-rotate(-10deg)',
    description: '☀️ SOLAR ECLIPSE MODE - Maximum solar protection with corona enhancement'
  },
  
  solar_eclipse_totality: {
    filter: 'brightness(0.15) contrast(3.0) saturate(1.8) hue-rotate(10deg)',
    description: '🌑 TOTALITY VIEW - Optimized for total solar eclipse corona visibility'
  },
  
  solar_eclipse_partial: {
    filter: 'brightness(0.05) contrast(4.0) saturate(0.1)',
    description: '🌓 PARTIAL ECLIPSE - Maximum dimming for partial phases'
  },
  
  solar_corona: {
    filter: 'brightness(0.12) contrast(3.8) saturate(2.5) hue-rotate(30deg)',
    description: '👑 CORONA ENHANCEMENT - Reveals solar corona structure'
  },
  
  solar_prominences: {
    filter: 'brightness(0.10) contrast(4.2) saturate(3.0) hue-rotate(-20deg)',
    description: '🔥 PROMINENCE VIEW - Highlights solar prominences and flares'
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // LUNAR ECLIPSE VIEWING MODES
  // Optimized for blood moon and penumbral phases
  // ═══════════════════════════════════════════════════════════════════════════════
  
  lunar_eclipse: {
    filter: 'brightness(1.8) contrast(1.6) saturate(2.2) hue-rotate(-5deg)',
    description: '🌕 LUNAR ECLIPSE - Enhanced blood moon visibility'
  },
  
  lunar_eclipse_blood_moon: {
    filter: 'brightness(2.2) contrast(1.8) saturate(3.0) hue-rotate(-15deg) sepia(0.2)',
    description: '🔴 BLOOD MOON - Maximum red enhancement for total lunar eclipse'
  },
  
  lunar_eclipse_penumbral: {
    filter: 'brightness(1.5) contrast(2.0) saturate(1.4)',
    description: '🌗 PENUMBRAL - Subtle shading enhancement for penumbral phases'
  },
  
  lunar_surface: {
    filter: 'brightness(1.4) contrast(2.2) saturate(0.8)',
    description: '🌙 LUNAR DETAIL - Crater and surface feature enhancement'
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // SOLAR DIMMING LEVELS - PROGRESSIVE INTENSITY
  // For general solar observation with proper equipment
  // ═══════════════════════════════════════════════════════════════════════════════
  
  sun_dimming_min: {
    filter: 'brightness(0.75) contrast(1.4) saturate(0.85)',
    description: 'MIN - Light solar dimming for sunrise/sunset'
  },
  sun_dimming_medium: {
    filter: 'brightness(0.50) contrast(1.8) saturate(0.7)',
    description: 'MEDIUM - Standard solar viewing'
  },
  sun_dimming_high: {
    filter: 'brightness(0.30) contrast(2.2) saturate(0.5)',
    description: 'HIGH - Bright sun conditions'
  },
  sun_dimming_max: {
    filter: 'brightness(0.15) contrast(2.8) saturate(0.3)',
    description: 'MAX - Direct solar observation'
  },
  sun_dimming_extreme: {
    filter: 'brightness(0.08) contrast(3.5) saturate(0.2)',
    description: 'EXTREME - Maximum protection'
  },
  
  // Legacy sun_dimming
  sun_dimming: {
    filter: 'brightness(0.50) contrast(1.8) saturate(0.7)',
    description: 'Solar Shield - Tap for intensity levels'
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // MILITARY-GRADE NIGHT VISION SYSTEMS
  // Based on Gen 3+ image intensifier technology simulation
  // ═══════════════════════════════════════════════════════════════════════════════
  
  night_mode: {
    filter: 'brightness(2.5) contrast(1.6) saturate(1.8) hue-rotate(-8deg)',
    description: '🌃 AI NIGHT MODE - Advanced low-light enhancement'
  },
  
  night_vision: {
    filter: 'brightness(3.0) contrast(1.8) saturate(0) sepia(1) hue-rotate(75deg) saturate(4)',
    description: '🔋 GEN 3+ NIGHT VISION - Military phosphor green simulation'
  },
  
  night_vision_white: {
    filter: 'brightness(3.2) contrast(2.0) saturate(0) invert(0.05)',
    description: '⚪ WHITE PHOSPHOR NV - Modern tactical night vision'
  },
  
  starlight: {
    filter: 'brightness(4.0) contrast(1.4) saturate(2.0)',
    description: '✨ STARLIGHT SCOPE - Maximum light amplification'
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // THERMAL IMAGING SYSTEMS
  // Multi-palette thermal simulation based on FLIR technology
  // ═══════════════════════════════════════════════════════════════════════════════
  
  thermal: {
    filter: 'brightness(1.4) contrast(2.5) saturate(0) sepia(1) hue-rotate(180deg) saturate(3)',
    description: '🌡️ THERMAL WHITE-HOT - Standard thermal imaging'
  },
  
  thermal_black_hot: {
    filter: 'brightness(1.4) contrast(2.5) saturate(0) invert(1)',
    description: '⬛ THERMAL BLACK-HOT - Inverted thermal palette'
  },
  
  thermal_ironbow: {
    filter: 'brightness(1.3) contrast(2.2) saturate(2.5) hue-rotate(-30deg)',
    description: '🌈 IRONBOW THERMAL - Multi-color heat signature'
  },
  
  thermal_rainbow: {
    filter: 'brightness(1.2) contrast(2.0) saturate(3.5) hue-rotate(0deg)',
    description: '🎨 RAINBOW THERMAL - Full spectrum heat visualization'
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // INFRARED SPECTRUM SIMULATION
  // Near-IR and Far-IR band simulation
  // ═══════════════════════════════════════════════════════════════════════════════
  
  infrared: {
    filter: 'brightness(1.5) contrast(1.8) saturate(0) sepia(1) hue-rotate(-40deg) saturate(2.5)',
    description: '🔴 NEAR-IR - Infrared spectrum simulation'
  },
  
  infrared_far: {
    filter: 'brightness(1.3) contrast(2.0) saturate(0) sepia(1) hue-rotate(-60deg) saturate(2)',
    description: '🟤 FAR-IR - Deep infrared simulation'
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // ASTRONOMICAL ENHANCEMENT FILTERS
  // AI-optimized for celestial observation
  // ═══════════════════════════════════════════════════════════════════════════════
  
  star_enhancement: {
    filter: 'brightness(1.8) contrast(2.5) saturate(2.0)',
    description: '⭐ STAR AMPLIFIER - AI-enhanced star visibility'
  },
  
  deep_sky: {
    filter: 'brightness(2.2) contrast(2.8) saturate(2.5) hue-rotate(10deg)',
    description: '🌌 DEEP SKY - Nebula and galaxy enhancement'
  },
  
  planet_enhance: {
    filter: 'brightness(1.3) contrast(2.2) saturate(1.8)',
    description: '🪐 PLANET VIEW - Planetary detail enhancement'
  },
  
  meteor_track: {
    filter: 'brightness(2.5) contrast(2.0) saturate(1.5)',
    description: '☄️ METEOR TRACKING - Streak detection enhancement'
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // ANOMALY DETECTION & ANALYSIS
  // AI-powered unidentified object detection
  // ═══════════════════════════════════════════════════════════════════════════════
  
  anomaly_highlight: {
    filter: 'contrast(3.0) saturate(0.4) brightness(1.5) hue-rotate(180deg)',
    description: '🛸 ANOMALY SCAN - AI anomaly detection mode'
  },
  
  ufo_track: {
    filter: 'contrast(2.8) saturate(0.6) brightness(1.8) hue-rotate(160deg)',
    description: '👽 UAP TRACKING - Unidentified aerial phenomena mode'
  },
  
  motion_detect: {
    filter: 'contrast(2.5) saturate(1.0) brightness(1.4)',
    description: '🎯 MOTION DETECT - Movement highlighting'
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // CLARITY & ENHANCEMENT SYSTEMS
  // Professional-grade image optimization
  // ═══════════════════════════════════════════════════════════════════════════════
  
  noise_reduction: {
    filter: 'contrast(1.3) saturate(1.15) brightness(1.08)',
    description: '🔇 AI NOISE REDUCTION - Crystal clear enhancement'
  },
  
  '4k_upscale': {
    filter: 'contrast(1.4) saturate(1.25) brightness(1.1)',
    imageRendering: 'crisp-edges',
    description: '📺 ULTRA 4K - Maximum resolution enhancement'
  },
  
  '8k_ultra': {
    filter: 'contrast(1.5) saturate(1.3) brightness(1.12)',
    imageRendering: 'crisp-edges',
    description: '🖥️ 8K ULTRA HD - Extreme clarity mode'
  },
  
  hdr_enhance: {
    filter: 'contrast(1.6) saturate(1.4) brightness(1.15)',
    description: '🌈 HDR+ - High dynamic range enhancement'
  },
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // MOTION ANALYSIS
  // Real-time motion amplification and tracking
  // ═══════════════════════════════════════════════════════════════════════════════
  
  motion_enhance: {
    filter: 'contrast(2.2) saturate(1.5) brightness(1.3)',
    description: '🏃 MOTION AMPLIFY - Micro-movement enhancement'
  },
  
  object_tracking: {
    filter: 'contrast(2.0) brightness(1.25) saturate(1.4)',
    description: '🎯 AI TRACKING - Object lock and track'
  },
  
  velocity_trace: {
    filter: 'contrast(1.8) saturate(1.2) brightness(1.2)',
    description: '💨 VELOCITY TRACE - Speed trail visualization'
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// SOLAR INTENSITY LEVELS
// ═══════════════════════════════════════════════════════════════════════════════

export const solarIntensityLevels = [
  { id: 'min', label: 'Min', brightness: 0.75, icon: '○', description: 'Sunrise/Sunset' },
  { id: 'medium', label: 'Med', brightness: 0.50, icon: '◐', description: 'Standard' },
  { id: 'high', label: 'High', brightness: 0.30, icon: '◑', description: 'Bright' },
  { id: 'max', label: 'Max', brightness: 0.15, icon: '◒', description: 'Direct' },
  { id: 'extreme', label: 'Extreme', brightness: 0.08, icon: '●', description: 'Maximum' }
];

// ═══════════════════════════════════════════════════════════════════════════════
// ECLIPSE MODE PRESETS
// ═══════════════════════════════════════════════════════════════════════════════

export const eclipsePresets = {
  solar: [
    { id: 'solar_eclipse_partial', label: 'Partial', icon: '🌓' },
    { id: 'solar_eclipse', label: 'Standard', icon: '🌑' },
    { id: 'solar_eclipse_totality', label: 'Totality', icon: '👑' },
    { id: 'solar_corona', label: 'Corona', icon: '☀️' },
    { id: 'solar_prominences', label: 'Flares', icon: '🔥' }
  ],
  lunar: [
    { id: 'lunar_eclipse_penumbral', label: 'Penumbral', icon: '🌗' },
    { id: 'lunar_eclipse', label: 'Standard', icon: '🌕' },
    { id: 'lunar_eclipse_blood_moon', label: 'Blood Moon', icon: '🔴' },
    { id: 'lunar_surface', label: 'Surface', icon: '🌙' }
  ]
};

// ═══════════════════════════════════════════════════════════════════════════════
// FILTER COMBINATION ENGINE
// Military-grade filter stacking algorithm
// ═══════════════════════════════════════════════════════════════════════════════

export const combineFilters = (activeFilters, solarLevel = 'medium') => {
  if (!activeFilters || activeFilters.length === 0) {
    return { 
      filter: 'none',
      imageRendering: 'high-quality',
      WebkitBackfaceVisibility: 'hidden'
    };
  }

  let brightness = 1;
  let contrast = 1;
  let saturate = 1;
  let hueRotate = 0;
  let sepia = 0;
  let invert = 0;

  activeFilters.forEach(filterName => {
    // Handle solar dimming with level
    let style;
    if (filterName === 'sun_dimming') {
      style = liveFilterStyles[`sun_dimming_${solarLevel}`] || liveFilterStyles.sun_dimming_medium;
    } else {
      style = liveFilterStyles[filterName];
    }
    
    if (!style?.filter) return;

    const filterStr = style.filter;
    
    const brightnessMatch = filterStr.match(/brightness\(([\d.]+)\)/);
    if (brightnessMatch) brightness *= parseFloat(brightnessMatch[1]);

    const contrastMatch = filterStr.match(/contrast\(([\d.]+)\)/);
    if (contrastMatch) contrast *= parseFloat(contrastMatch[1]);

    const saturateMatches = filterStr.matchAll(/saturate\(([\d.]+)\)/g);
    for (const match of saturateMatches) {
      saturate *= parseFloat(match[1]);
    }

    const hueMatch = filterStr.match(/hue-rotate\(([-\d.]+)deg\)/);
    if (hueMatch) hueRotate += parseFloat(hueMatch[1]);

    const sepiaMatch = filterStr.match(/sepia\(([\d.]+)\)/);
    if (sepiaMatch) sepia = Math.max(sepia, parseFloat(sepiaMatch[1]));
    
    const invertMatch = filterStr.match(/invert\(([\d.]+)\)/);
    if (invertMatch) invert = Math.max(invert, parseFloat(invertMatch[1]));
  });

  // Clamp values for optimal output
  brightness = Math.max(0.05, Math.min(5, brightness));
  contrast = Math.max(0.5, Math.min(5, contrast));
  saturate = Math.max(0, Math.min(5, saturate));
  hueRotate = hueRotate % 360;
  sepia = Math.min(1, sepia);
  invert = Math.min(1, invert);

  let filterParts = [];
  if (brightness !== 1) filterParts.push(`brightness(${brightness.toFixed(3)})`);
  if (contrast !== 1) filterParts.push(`contrast(${contrast.toFixed(3)})`);
  if (saturate !== 1) filterParts.push(`saturate(${saturate.toFixed(3)})`);
  if (hueRotate !== 0) filterParts.push(`hue-rotate(${hueRotate.toFixed(1)}deg)`);
  if (sepia > 0) filterParts.push(`sepia(${sepia.toFixed(3)})`);
  if (invert > 0) filterParts.push(`invert(${invert.toFixed(3)})`);

  return {
    filter: filterParts.length > 0 ? filterParts.join(' ') : 'none',
    transition: 'filter 0.2s ease-out',
    imageRendering: 'high-quality',
    WebkitBackfaceVisibility: 'hidden',
    transform: 'translateZ(0)',
    willChange: 'filter'
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// OVERLAY EFFECTS ENGINE
// Advanced visual overlay system
// ═══════════════════════════════════════════════════════════════════════════════

export const getFilterOverlay = (activeFilters, themeColors) => {
  const overlays = [];

  // Solar filters - concentric ring overlay
  if (activeFilters.some(f => f.includes('sun_dimming') || f.includes('solar'))) {
    overlays.push({
      type: 'solar_shield',
      style: {
        background: 'radial-gradient(circle at center, transparent 20%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.6) 80%, rgba(0,0,0,0.8) 100%)',
        mixBlendMode: 'multiply'
      }
    });
  }

  // Eclipse modes - corona simulation
  if (activeFilters.some(f => f.includes('eclipse') || f.includes('corona'))) {
    overlays.push({
      type: 'corona',
      style: {
        background: 'radial-gradient(circle at center, transparent 15%, rgba(255,200,100,0.1) 30%, rgba(255,150,50,0.05) 50%, transparent 70%)',
        mixBlendMode: 'screen'
      }
    });
  }

  // Night vision - phosphor scanlines + vignette
  if (activeFilters.includes('night_vision') || activeFilters.includes('night_vision_white')) {
    overlays.push({
      type: 'nv_scanlines',
      style: {
        backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(57, 255, 20, 0.03) 1px, rgba(57, 255, 20, 0.03) 2px)`,
        boxShadow: 'inset 0 0 150px rgba(57, 255, 20, 0.2), inset 0 0 50px rgba(0,0,0,0.5)'
      }
    });
  }

  // Starlight - subtle glow
  if (activeFilters.includes('starlight')) {
    overlays.push({
      type: 'starlight_glow',
      style: {
        boxShadow: 'inset 0 0 100px rgba(200, 220, 255, 0.1)'
      }
    });
  }

  // Thermal imaging - heat gradient
  if (activeFilters.some(f => f.includes('thermal'))) {
    overlays.push({
      type: 'thermal_gradient',
      style: {
        background: 'linear-gradient(180deg, rgba(255,0,0,0.08) 0%, rgba(255,200,0,0.04) 30%, rgba(0,255,0,0.02) 60%, rgba(0,0,255,0.08) 100%)',
        mixBlendMode: 'overlay'
      }
    });
  }

  // Infrared - red vignette
  if (activeFilters.some(f => f.includes('infrared'))) {
    overlays.push({
      type: 'ir_vignette',
      style: {
        boxShadow: 'inset 0 0 100px rgba(255, 80, 0, 0.25)'
      }
    });
  }

  // Object tracking - targeting reticle effect
  if (activeFilters.includes('object_tracking') || activeFilters.includes('ufo_track')) {
    overlays.push({
      type: 'targeting',
      style: {
        border: `2px solid ${themeColors?.primary || '#00f2ea'}`,
        boxShadow: `inset 0 0 40px ${themeColors?.primary || '#00f2ea'}30, 0 0 20px ${themeColors?.primary || '#00f2ea'}20`
      }
    });
  }

  // Anomaly detection - scan effect
  if (activeFilters.includes('anomaly_highlight') || activeFilters.includes('motion_detect')) {
    overlays.push({
      type: 'anomaly_scan',
      style: {
        boxShadow: 'inset 0 0 80px rgba(255, 0, 150, 0.15)'
      }
    });
  }

  // Blood moon - red ambient
  if (activeFilters.includes('lunar_eclipse_blood_moon')) {
    overlays.push({
      type: 'blood_moon',
      style: {
        boxShadow: 'inset 0 0 120px rgba(180, 0, 0, 0.2)'
      }
    });
  }

  // Deep sky - cosmic dust
  if (activeFilters.includes('deep_sky')) {
    overlays.push({
      type: 'cosmic',
      style: {
        boxShadow: 'inset 0 0 100px rgba(100, 0, 200, 0.1)'
      }
    });
  }

  return overlays;
};

// ═══════════════════════════════════════════════════════════════════════════════
// FILTER CATEGORIES FOR UI
// ═══════════════════════════════════════════════════════════════════════════════

export const filterCategories = {
  eclipse: {
    label: '🌑 Eclipse',
    filters: ['solar_eclipse', 'solar_eclipse_totality', 'solar_eclipse_partial', 'solar_corona', 'solar_prominences', 
              'lunar_eclipse', 'lunar_eclipse_blood_moon', 'lunar_eclipse_penumbral', 'lunar_surface']
  },
  solar: {
    label: '☀️ Solar',
    filters: ['sun_dimming']
  },
  nightVision: {
    label: '🔋 Night Vision',
    filters: ['night_mode', 'night_vision', 'night_vision_white', 'starlight']
  },
  thermal: {
    label: '🌡️ Thermal/IR',
    filters: ['thermal', 'thermal_black_hot', 'thermal_ironbow', 'thermal_rainbow', 'infrared', 'infrared_far']
  },
  astronomical: {
    label: '⭐ Astronomical',
    filters: ['star_enhancement', 'deep_sky', 'planet_enhance', 'meteor_track']
  },
  detection: {
    label: '🛸 Detection',
    filters: ['anomaly_highlight', 'ufo_track', 'motion_detect', 'object_tracking', 'velocity_trace']
  },
  enhancement: {
    label: '📺 Enhancement',
    filters: ['noise_reduction', '4k_upscale', '8k_ultra', 'hdr_enhance', 'motion_enhance']
  }
};

export default {
  liveFilterStyles,
  solarIntensityLevels,
  eclipsePresets,
  combineFilters,
  getFilterOverlay,
  filterCategories
};
